<template>
  <section :style="{ backgroundColor: props.backgroundColor }">
    <div class="px-4 py-10 mx-auto max-w-5xl md:py-14 xl:px-0">
      <h4 :class="newStyle ? 'xl:text-42 text-4xl' : 'xl:text-42 md:text-4xl text-3xl'"
        class="font-bold text-center lg:leading-10 font-ww-bold">
        {{ props.title }}
      </h4>

      <div class="mt-8 lg:mt-16 lg:mb-6" :class="newStyle ? 'md:space-y-6 space-y-4' : 'space-y-6'">
        <div v-for="(item, i) in props.items" :key="i" class="bg-[#F5F5F5] rounded-lg lg:px-10 lg:py-8"
          :class="newStyle ? 'md:p-5 p-10' : 'p-5'">
          <button :id="`faq-${i}-btn`"
            class="flex justify-between items-center w-full text-left rounded-xl group focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800"
            @click="toggle(i)" @keydown.enter.prevent="toggle(i)" @keydown.space.prevent="toggle(i)"
            :aria-expanded="isOpen(i)" :aria-controls="`faq-${i}-panel`">
            <span :class="newStyle ? 'text-21' : 'md:text-xl lg:text-21 text-lg'"
              class="font-bold md-max:leading-6 font-ww-bold" v-html="item.q"></span>



            <span class="inline-flex justify-center items-center ml-4 w-6 h-6" aria-hidden="true">

              <svg v-if="!isOpen(i)" width="19" height="19" viewBox="0 0 19 19" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M0.648438 9.55664H18.6211" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
                <path d="M9.63281 0.570312V18.543" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
              </svg>
              <svg v-else width="19" height="3" viewBox="0 0 19 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.648438 1.55664H18.6211" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
              </svg>
            </span>
          </button>


          <transition @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave"
            enter-active-class="transition-all duration-500 ease-out"
            leave-active-class="transition-all duration-500 ease-in">
            <div v-show="isOpen(i)" :id="`faq-${i}-panel`"
              :class="newStyle ? 'xl:text-21 text-xl md-max:!leading-8 md-max:!mt-8' : 'xl:text-21 md:text-lg text-base'"
              class="mt-3 xl:!leading-8 opacity-0 md:mt-6 font-ww-regular xl:pr-20" role="region"
              :aria-labelledby="`faq-${i}-btn`" v-html="item.a">

            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>

// Props for Builder/usage
const props = defineProps({
  newStyle: { type: Boolean, default: false },
  title: {
    type: String,
    default: 'Frequently Asked Questions'
  },
  backgroundColor: {
    type: String,
    default: '#F2F2F2'
  },
  items: {
    type: Array,
    default: () => ([
      {
        q: 'What if I don\'t like it?',
        a: 'We want you to love your coffee! If you don\'t, just let us know and we\'ll take care of you. (Placeholder text)'
      },
      {
        q: 'Why is Lifeboost more expensive than grocery store coffee?',
        a: 'Lifeboost Coffee is single-origin, organic, low-acid, and third-party tested for toxins. We prioritize quality and health benefits which come at a higher cost of production compared to mass-produced grocery store coffee. (Placeholder text)'
      },
      {
        q: 'Who is Lifeboost Coffee best for?',
        a: 'Lifeboost is perfect for health-conscious coffee lovers who experience stomach issues with regular coffee, anyone concerned about pesticides and mold in their coffee, and those who simply want the cleanest, best-tasting coffee available. Our Starter plan is ideal for individuals, Daily Ritual for couples, and Coffee Enthusiast for families.'
      }
    ])
  }
})

// Currently open panel (single accordion)
const openIndex = ref(null)

const isOpen = (i) => openIndex.value === i
const toggle = (i) => {
  if (openIndex.value === i) {
    openIndex.value = null
  } else {
    openIndex.value = i
  }
}

// Smooth height and opacity animation
function onEnter(el) {
  el.style.height = '0px'
  el.style.overflow = 'hidden'
  el.style.opacity = '0'
  el.style.transition = 'height 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'

  // Force reflow, then animate to real height
  requestAnimationFrame(() => {
    el.style.height = el.scrollHeight + 'px'
    el.style.opacity = '1'
  })
}

function onAfterEnter(el) {
  el.style.height = 'auto'
  el.style.overflow = 'visible'
  el.style.opacity = '1'
}

function onLeave(el) {
  el.style.height = el.scrollHeight + 'px'
  el.style.overflow = 'hidden'
  el.style.opacity = '1'
  el.style.transition = 'height 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'

  // After one frame, animate to 0 for closing animation
  requestAnimationFrame(() => {
    el.style.height = '0px'
    el.style.opacity = '0'
  })
}

defineBuilderComponent({
  friendlyName: 'FAQ Accordion Basic',
  inputs: [
    {
      name: 'newStyle',
      type: 'boolean',
      defaultValue: false,
      friendlyName: 'New Style'
    },
    {
      name: 'title',
      type: 'text',
      defaultValue: 'Frequently Asked Questions'
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: '#F2F2F2'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        { name: 'q', type: 'text', required: true },
        { name: 'a', type: 'richText', required: true }
      ],
      defaultValue: [
        {
          q: 'What if I don\'t like it?',
          a: 'We want you to love your coffee! If you don\'t, just let us know and we\'ll take care of you. (Placeholder text)'
        },
        {
          q: 'Why is Lifeboost more expensive than grocery store coffee?',
          a: 'Lifeboost Coffee is single-origin, organic, low-acid, and third-party tested for toxins. We prioritize quality and health benefits which come at a higher cost of production compared to mass-produced grocery store coffee. (Placeholder text)'
        },
        {
          q: 'Who is Lifeboost Coffee best for?',
          a: 'Lifeboost is perfect for health-conscious coffee lovers who experience stomach issues with regular coffee, anyone concerned about pesticides and mold in their coffee, and those who simply want the cleanest, best-tasting coffee available. Our Starter plan is ideal for individuals, Daily Ritual for couples, and Coffee Enthusiast for families.'
        }
      ]
    }
  ]
})
</script>
